import { Box, Popover, Tab, Tabs, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import useAPI from "../../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../../api/hooks/useColors";
import {
  entityKeys,
  vulnerabilityKeys,
} from "../../../../../../../api/queryKeys";
import PrimaryButton from "../../../../../../../components/buttons/PrimaryButton";
import LoadingIcon from "../../../../../../../components/LoadingIcon";
import { SLA_CRITICAL, SLA_HIGH } from "../../../../../../../utils/constants";
import {
  getPrettyDate,
  useLocalTime,
} from "../../../../../../../utils/timestamp";
import { isLocalhost } from "../../../../../../../utils/utils";
import { filterName } from "../../../../filters/utils";
import useChartSeries from "./chartSeries";
import { NoDataMessage } from "./NoDataMessage";
import SlaBreakdown from "./SlaBreakdown";
import SLAChartCustomBar from "./SLAChartCustomBar";
import CvesSLAChartLegend from "./VulnerabilitySLAChartLegend";

const ManagementSLAChart = ({
  entities,
  appliedFilters,
  setAppliedFilters,
}) => {
  const [tabIndex, setTabIndex] = useState(0);
  const colors = useColors();
  const showLocalTime = useLocalTime();
  const [openPopover, setOpenPopover] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const series = useChartSeries();
  const [, setChartsReady] = useState(false);

  const {
    fetchAssociationsByEntityType,
    trackEvent,
    fetchVulnerabilitiesByEntityType,
    getCrowdstrikeCveFirstCollect,
  } = useAPI();

  // Check if running in localhost
  const isLocalhostEnv = isLocalhost();
  const [cvesFetchDisabled, setCvesFetchDisabled] = useState(true);

  const vendorTypesBrowser = ["Safari", "Chrome", "Firefox", "Edge"];

  const getStack = (vendorType) => {
    if (
      vendorType?.toLowerCase().includes("windows") ||
      vendorType?.toLowerCase().includes("mac")
    ) {
      return "OS";
    } else if (vendorTypesBrowser.includes(vendorType)) {
      return "browser";
    } else {
      return "thirdparty";
    }
  };

  const hostEntityIds = entities
    .filter((entity) => entity.entity_type === "host")
    .map((entity) => entity.entity_id);

  const { data: firstTimeCollect, isLoading: firstTimeCollectIsLoading } =
    useQuery({
      queryKey: vulnerabilityKeys.crowdstrikeFirstCollectVulnerabilities(),
      queryFn: () => {
        return getCrowdstrikeCveFirstCollect();
      },
      select: (response) => response?.data,
    });

  const checkMinSlaPassed = (minSlaDays) => {
    if (!firstTimeCollect) return false;
    const minFoundOnDate = firstTimeCollect?.timestamp_utc;
    if (!minSlaDays) return false;
    const now = new Date();
    const todayUtc =
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) /
      1000; // Convert to seconds
    const diffDays = (todayUtc - minFoundOnDate) / (60 * 60 * 24); // 1 day in milliseconds
    return diffDays > minSlaDays;
  };

  const {
    data: entityHostVulnerabilities,
    isLoading: entityHostVulnerabilitiesIsLoading,
    refetch: refetchVulnerabilities,
  } = useQuery({
    queryKey: entityKeys.vulnerabilitiesByEntityType("host"),
    queryFn: () => {
      return fetchVulnerabilitiesByEntityType("host");
    },
    select: (response) => response?.data,
    enabled:
      checkMinSlaPassed(SLA_CRITICAL) &&
      (!isLocalhostEnv || !cvesFetchDisabled) &&
      !firstTimeCollectIsLoading,
  });

  const {
    data: entityHostAssociations,
    isLoading: entityHostAssociationsIsLoading,
    refetch: refetchAssociations,
  } = useQuery({
    queryKey: entityKeys.associationsByEntityType("host"),
    queryFn: () => {
      return fetchAssociationsByEntityType("host");
    },
    select: (response) => response?.data,
    enabled:
      (!isLocalhostEnv || !cvesFetchDisabled) &&
      !firstTimeCollectIsLoading &&
      checkMinSlaPassed(SLA_CRITICAL),
  });

  const entityHostAssociationsFiltered = useMemo(() => {
    if (!entityHostAssociations) return null;
    return entityHostAssociations.filter((association) =>
      hostEntityIds.includes(association.entity_id)
    );
  }, [entityHostAssociations, hostEntityIds]);

  const entityHostAssociationsWithVendor = useMemo(() => {
    if (entityHostVulnerabilitiesIsLoading || entityHostAssociationsIsLoading) {
      return [];
    }
    const associations = [];
    entityHostAssociationsFiltered?.forEach((association) => {
      const entityVulnerability =
        entityHostVulnerabilities[association.entity_id]?.find(
          (vulnerability) =>
            vulnerability.vulnerability_id === association.vulnerability_id
        ) || [];

      if (entityVulnerability) {
        associations.push({
          ...association,
          type: getStack(entityVulnerability.vendor_type),
        });
      }
    });
    return associations;
  }, [entityHostAssociationsFiltered, entityHostVulnerabilities]);

  function mapToPercentageDataset(data, showLocalTime) {
    const grouped = {};

    data.forEach((item) => {
      const found_on_date = getPrettyDate(
        item?.found_on_date?.timestamp_utc,
        showLocalTime
      );
      if (!found_on_date) return; // Skip if found_on_date is not available
      const month = found_on_date;
      const typeKey = item.type?.toLowerCase().replace(/\s/g, ""); // os, browser, 3rdparty
      const severity = item.vulnerability_severity?.toLowerCase();

      // Determine SLA based on severity
      const slaDays =
        severity === "critical"
          ? SLA_CRITICAL
          : severity === "high"
          ? SLA_HIGH
          : null;

      // Default: Not within SLA if no SLA definition
      let isWithinSLA = false;

      if (slaDays !== null) {
        const now = new Date();
        const todayUtc =
          Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) /
          1000; // Convert to seconds

        const resolved = item.resolved_date
          ? item?.resolved_date?.timestamp_utc
          : todayUtc;
        const diffDays =
          (resolved - item?.found_on_date?.timestamp_utc) / (60 * 60 * 24); // 1 day in milliseconds
        // Check if the difference in days is within SLA
        isWithinSLA = diffDays <= slaDays;
      }

      // Init month bucket
      if (!grouped[month]) {
        grouped[month] = {
          month,
          os: { within: 0, not_within: 0 },
          browser: { within: 0, not_within: 0 },
          thirdparty: { within: 0, not_within: 0 },
          entity_ids: [],
        };
      }

      const category = grouped[month][typeKey];
      if (category) {
        if (isWithinSLA) category.within++;
        else category.not_within++;
      }
      grouped[month].entity_ids.push(item.entity_id);
    });

    // Convert to percentage format
    return Object.values(grouped).map((monthData) => {
      const { month, os, browser, thirdparty, entity_ids } = monthData;

      const percent = (w, nw) => {
        const total = w + nw;
        return total > 0
          ? {
              within: Math.round((w / total) * 100),
              not_within: Math.round((nw / total) * 100),
            }
          : { within: 0, not_within: 0 };
      };

      const osPerc = percent(os.within, os.not_within);
      const browserPerc = percent(browser.within, browser.not_within);
      const thirdPartyPerc = percent(thirdparty.within, thirdparty.not_within);

      return {
        month,
        os_within: osPerc.within,
        os_not_within: osPerc.not_within,
        browser_within: browserPerc.within,
        browser_not_within: browserPerc.not_within,
        thirdparty_within: thirdPartyPerc.within,
        thirdparty_not_within: thirdPartyPerc.not_within,
        entity_ids: entity_ids,
      };
    });
  }

  const fullDataset = useMemo(
    () =>
      mapToPercentageDataset(entityHostAssociationsWithVendor, showLocalTime),
    [entityHostAssociationsWithVendor]
  );

  // Filter to show only the last 6 months
  const dataset = useMemo(() => {
    // Get current month and year
    const now = new Date();
    const currentMonth = now.toLocaleString("en-US", { month: "long" });
    const currentYear = now.getFullYear();

    // Get minimum found date from firstTimeCollect
    const minFoundOnDate = firstTimeCollect?.timestamp_utc;

    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    // Sort by date (assuming month is in "Month, Year" format)
    const sortedData = [...fullDataset].sort((a, b) => {
      // Parse month strings like "March, 2025" into Date objects
      const monthA = a.month.split(",")[0].trim();
      const yearA = parseInt(a.month.split(",")[1].trim());
      const monthB = b.month.split(",")[0].trim();
      const yearB = parseInt(b.month.split(",")[1].trim());

      // Compare years first
      if (yearA !== yearB) return yearB - yearA;
      // If years are the same, compare months
      return months.indexOf(monthB) - months.indexOf(monthA);
    });

    // Filter out current month and dates before minFoundOnDate
    const filteredData = sortedData.filter((item) => {
      const month = item.month.split(",")[0].trim();
      const year = parseInt(item.month.split(",")[1].trim());

      // Skip current month
      if (month === currentMonth && year === currentYear) {
        return false;
      }

      // Skip dates before minFoundOnDate
      if (minFoundOnDate) {
        const monthIndex = months.indexOf(month);

        // Convert item date to timestamp for comparison
        const itemDate = new Date(Date.UTC(year, monthIndex, 1));
        const itemTimestamp = Math.floor(itemDate.getTime() / 1000);

        // Convert minFoundOnDate (seconds) to beginning of the month
        const minDate = new Date(minFoundOnDate * 1000);
        const minDateMonthStart = new Date(
          Date.UTC(minDate.getUTCFullYear(), minDate.getUTCMonth(), 1)
        );
        const minTimestamp = Math.floor(minDateMonthStart.getTime() / 1000);

        return itemTimestamp >= minTimestamp;
      }

      return true;
    });

    // Return only the last 3 months or all if less than 3
    return filteredData.slice(0, Math.min(3, filteredData.length));
  }, [fullDataset, firstTimeCollect]);

  const cvesTabData = [
    {
      tabTitle: "Overview",
      title: "SLA Performance - Vulnerability Management",
      series: series,
      dataset: dataset,
      tabIndex: 0,
    },
    {
      tabTitle: "OS CVEs",
      title: "SLA Performance - OS Vulnerability Management",
      series: series.filter((item) => item.dataKey.includes("os")),
      dataset: dataset.map((item) => ({
        ...item,
        os_within: item.os_within,
        os_not_within: item.os_not_within,
      })),
      tabIndex: 1,
    },
    {
      tabTitle: "Browser CVEs",
      title: "SLA Performance - Browser Vulnerability Management",
      series: series.filter((item) => item.dataKey.includes("browser")),
      dataset: dataset.map((item) => ({
        ...item,
        browser_within: item.browser_within,
        browser_not_within: item.browser_not_within,
      })),
      tabIndex: 2,
      averageHeader: true,
    },
    {
      tabTitle: "3rd Party CVEs",
      title: "SLA Performance - thirdparty Vulnerability Management",
      series: series.filter((item) => item.dataKey.includes("thirdparty")),
      dataset: dataset.map((item) => ({
        ...item,
        thirdparty_within: item.thirdparty_within,
        thirdparty_not_within: item.thirdparty_not_within,
      })),
      tabIndex: 3,
    },
  ];

  // Set charts as ready after component is fully mounted
  useEffect(() => {
    const timer = setTimeout(() => {
      setChartsReady(true);
    }, 500); // Small delay to ensure chart rendering is complete

    return () => clearTimeout(timer);
  }, []);

  const CustomBarLabel = (props) => {
    // Find the bar width to properly center the label
    const x = props.style.x.animation.to + "px";
    const y = props.style.y.animation.to + "px";

    // Find the corresponding series item to get the label color
    const seriesItem = series.find((item) => item.id === props.seriesId);
    const labelColor = seriesItem?.labelColor || colors.text_primary;

    return (
      <text
        style={{
          font: "10px Poppins, sans-serif",
          fill: labelColor,
          transform: `translate3d(${x},${y}, 0px)`,
          textAnchor: "middle", // Center the text horizontally
          dominantBaseline: "hanging", // Align text to the top (hanging from the y-coordinate)
        }}
      >
        {typeof props.children === "string"
          ? `${props.children}%`
          : `${props.children.label}%`}
      </text>
    );
  };

  const onBarClick = (event, clickedBarData) => {
    const groupIndex = clickedBarData.dataIndex;
    const month = dataset[groupIndex].month;
    const entity_ids = dataset[groupIndex].entity_ids; // entity_ids of the group
    const newFilters = entity_ids?.reduce(
      (acc, entity_id) => ({ ...acc, [entity_id]: true }),
      {}
    );
    trackEvent("user clicked on SLA chart and filtered by month: " + month);
    setAppliedFilters({
      ...appliedFilters,

      [filterName.ENTITY_IDS]: {
        ...appliedFilters[filterName.ENTITY_IDS],
        ...newFilters,
      },
    });
  };

  // Add a function to force repaint when tab changes
  const handleTabChange = (e, newIndex) => {
    setTabIndex(newIndex);
    // Force a small delay to ensure charts rerender completely
    setChartsReady(false);
    setTimeout(() => {
      setChartsReady(true);
    }, 100);
  };

  return (
    <Box>
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        textColor="primary"
        TabIndicatorProps={{
          style: {
            backgroundColor: colors.primary_main,
          },
        }}
        sx={{
          width: "100%",

          "& .MuiTabs-flexContainer": {
            backgroundColor: colors.common_white,
          },
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 400,
            color: colors.text_secondary,
            padding: "12px 16px",
          },
        }}
      >
        {cvesTabData.map((chartData, index) => (
          <Tab
            key={index}
            label={chartData.tabTitle}
            sx={{
              "&.Mui-selected": {
                color: colors.text_primary,
                fontWeight: 500,
              },
            }}
          />
        ))}
      </Tabs>
      <Box sx={{ p: 3 }}>
        {isLocalhostEnv && cvesFetchDisabled && (
          <Box sx={{ mb: 3, textAlign: "center" }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              SLA data fetching is currently disabled in localhost environment.
            </Typography>
            <PrimaryButton
              variant="contained"
              onClick={() => {
                setCvesFetchDisabled(false);
                refetchVulnerabilities();
                refetchAssociations();
                trackEvent("User enabled SLA data fetching in localhost");
              }}
            >
              Enable SLA Data Fetching
            </PrimaryButton>
          </Box>
        )}
        {cvesTabData.map((chartData, index) => {
          return (
            <Box key={index}>
              {tabIndex === chartData.tabIndex && (
                <Box
                  sx={{
                    background: colors.common_white,
                    boxShadow: "0px 0px 6.3px 0px " + colors.grey_24percent,
                    borderRadius: "8px",
                    width: "100%",
                    height: "430px",
                    p: 2,
                  }}
                >
                  {/* Title */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 2,
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="subtitle1">
                        {chartData.title}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        cursor: "pointer",
                        width: "fit-content",
                      }}
                      onClick={(e) => {
                        setAnchorEl(e.currentTarget);
                        setOpenPopover(true);
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.primary_main,
                          fontWeight: 500,
                        }}
                      >
                        SLA Breakdown
                      </Typography>
                    </Box>
                    <Popover
                      open={openPopover}
                      anchorEl={anchorEl}
                      onClose={() => setOpenPopover(false)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                      PaperProps={{
                        style: {
                          borderRadius: 10,
                          background: colors.common_white,
                        }, // Change the value as needed
                      }}
                    >
                      <SlaBreakdown />
                    </Popover>
                  </Box>
                  {firstTimeCollectIsLoading ? (
                    <LoadingIcon size={12} />
                  ) : cvesFetchDisabled && isLocalhostEnv ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "300px",
                      }}
                    >
                      <Typography variant="body1">
                        SLA data fetching is disabled in localhost environment.
                      </Typography>
                    </Box>
                  ) : !checkMinSlaPassed(SLA_CRITICAL) ? (
                    <NoDataMessage />
                  ) : entityHostAssociationsIsLoading ||
                    entityHostVulnerabilitiesIsLoading ? (
                    <LoadingIcon size={12} />
                  ) : dataset.length === 0 ? (
                    <NoDataMessage />
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-start",
                        pt: 1,
                      }}
                    >
                      <BarChart
                        onItemClick={onBarClick}
                        dataset={chartData.dataset}
                        sx={{
                          "& .MuiChartsAxis-tick": {
                            strokeWidth: 0,
                          },
                          "& .MuiChartsAxis-left .MuiChartsAxis-line": {
                            strokeWidth: 0,
                          },
                          "& .MuiChartsAxis-bottom .MuiChartsAxis-line": {
                            strokeWidth: 0,
                          },

                          "& .MuiChartsAxisHighlight-root": {
                            fill: "none",
                          }, // color both axis labels
                          "& .MuiChartsAxis-root .MuiChartsAxis-tickLabel": {
                            fill: colors.text_secondary,
                          },
                        }}
                        xAxis={[
                          {
                            scaleType: "band",
                            dataKey: "month",
                            position: "bottom",
                            categoryGapRatio: 0.4, // Controls spacing between month categories
                          },
                        ]}
                        series={chartData.series}
                        slotProps={{ legend: { hidden: true } }}
                        yAxis={[
                          {
                            min: 0,
                            max: 100,
                            valueFormatter: (value) => `${value}%`,
                          },
                        ]}
                        width={1000}
                        height={350}
                        borderRadius={8}
                        barLabel="value"
                        slots={{
                          barLabel: CustomBarLabel,
                          bar: SLAChartCustomBar,
                        }}
                        grid={{ horizontal: true }}
                        tooltip={{ trigger: "none" }}
                      />
                      <CvesSLAChartLegend series={chartData.series} />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ManagementSLAChart;
